import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Link, Rule, supportAddress, ui } from '../ui';
import { formatDate } from '@goodbuild/rules/format';
import { endSession, useSession } from '../auth';
import { revokeThisDevice } from '../launcher-device';
import { ApiError, useApi, useApiMutation } from '../api';
import { Screen, Table } from './shared';

/**
 * Your account.
 *
 * This replaces a `Sign out` button that sat in the navigation next to a name.
 * The button was the only account surface this product had, which meant the
 * one thing you could do with your account was leave it — and it put the most
 * destructive action available permanently one stray click from every page,
 * beside links people use constantly.
 *
 * What was actually missing is everything the button was standing in for:
 * which address you are signed in as and whether it is verified, what this
 * account can reach, and — the one that had no surface anywhere — the machines
 * running the launcher that hold a long-lived token for you. `/v1/launcher/devices`
 * has been able to list and revoke those since device tokens were written, and
 * no screen in any application called it, so a player who lost a laptop had no
 * way to sign it out short of changing their password.
 *
 * Signing out is still here. It is at the bottom, under a sentence saying what
 * it does and does not do, which is where an irreversible action belongs.
 */

/** See `standingFor` in `apps/api/src/lib/roles`. */
type RoleStanding = 'none' | 'applied' | 'active' | 'suspended';

interface Me {
  userId: string;
  via: 'session' | 'device';
  isAdmin: boolean;
  creator: { handle: string; displayName: string; state: string } | null;
  standing: {
    creator: RoleStanding;
    admin: RoleStanding;
    creatorHandle: string | null;
  };
}

interface Device {
  id: string;
  name: string;
  /** Null until the machine has checked in once. */
  lastSeenAt: string | null;
  createdAt: string;
}

export function AccountRoute(): React.ReactElement {
  const { data: session } = useSession();
  const query = useApi<Me>(['me'], '/me');
  const user = session?.user;

  return (
    <Screen title="Account" aside={user?.email ?? ''} query={query}>
      <Table head={['', '']}>
        <tr>
          <td>Name</td>
          <td>{user?.name || 'Not set'}</td>
        </tr>
        <tr>
          <td>Email</td>
          <td>
            {user?.email ?? '—'}
            {user && !user.emailVerified ? (
              <>
                {' — '}
                {/*
                  Stated rather than nagged about. An unverified address still
                  signs in; what it cannot do is claim a First Hundred place
                  (section 5.5), and knowing that is more use than a banner.
                */}
                <span className={ui.statLabel ?? ''}>not verified</span>
              </>
            ) : null}
          </td>
        </tr>
        <tr>
          <td>Signed in since</td>
          <td className={ui.numeric ?? ''}>
            {session?.session.createdAt ? formatDate(new Date(session.session.createdAt)) : '—'}
          </td>
        </tr>
      </Table>

      <Roles me={query.data} />
      <Reach me={query.data} />
      <Help />
      <Devices />
      <SignOut />
    </Screen>
  );
}

/**
 * What this account is, and how to become the rest of it.
 *
 * **There was nowhere to see this and nowhere to do it.** Whether your creator
 * application had been looked at, whether you were staff — none of it had a
 * surface. The only route to the application was `/onboarding`, which picks
 * what to show from the *hostname*, so the way to apply as a creator was to
 * already know that `creators.goodbuild.cc` exists and to type it. Somebody who
 * had applied and was waiting had no way to find out that anything had happened
 * short of trying the board and seeing whether it let them in.
 *
 * **There were three roles and now there are two** (#144). `Developer` was the
 * first row: self-serve, granted by registering a studio, and it named the
 * studios on the account underneath. There are no studios.
 *
 *   — **Content creator** is an approval, because the opportunity board carries
 *     rates that are not ours to show everybody. So it has a fourth state -
 *     applied — and the handle is held while somebody decides, which is the
 *     thing worth saying to a person who is waiting.
 *   — **Admin** is neither. It is granted by somebody who already holds it,
 *     there is no queue and no form, so it appears only when it is held. A row
 *     saying "you are not an administrator" is either noise or an invitation to
 *     ask for it, and it must be neither.
 *
 * The states come from `standing` rather than being inferred here. The
 * inference the app had was wrong: `creator` is null for an applied profile, so
 * an application in progress looked exactly like never having applied.
 */
function Roles({ me }: { me: Me | undefined }): React.ReactElement | null {
  if (!me) return null;

  const rows: {
    role: string;
    standing: RoleStanding;
    /** What it means here, not what the word means. */
    what: string;
    /** Where to go when it is not held. Null for a role nobody applies for. */
    apply: { href: string; label: string } | null;
    /** One fact under the status, when there is one worth having. */
    note?: string | undefined;
  }[] = [
    {
      role: 'Content creator',
      standing: me.standing.creator,
      what: 'Earn a cut of the sales your videos bring in.',
      apply: { href: '/onboarding/creator', label: 'Apply' },
      note: me.standing.creatorHandle ? `goodbuild.cc/@${me.standing.creatorHandle}` : undefined,
    },
  ];

  // Only when it is held. See above: there is nothing to apply for.
  if (me.standing.admin === 'active') {
    rows.push({
      role: 'Admin',
      standing: 'active',
      what: 'Staff screens.',
      apply: null,
    });
  }

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Who you are</h2>
      <Table head={['Role', 'What it means', 'Where you are']}>
        {rows.map((row) => (
          <tr key={row.role}>
            <td>{row.role}</td>
            <td>
              {row.what}
              {row.note ? (
                <>
                  <br />
                  <span className={ui.statLabel ?? ''}>{row.note}</span>
                </>
              ) : null}
            </td>
            <td>
              <Standing standing={row.standing} apply={row.apply} />
            </td>
          </tr>
        ))}
      </Table>
    </>
  );
}

/**
 * Where somebody stands on one role, and the one thing to do about it.
 *
 * Four states and four different sentences. `applied` is the one that earns
 * this component: it is not a failure and it is not a success, it needs no
 * button, and a page that drew an `Apply` control beside it would invite
 * somebody to apply a second time and collide with the handle they are already
 * holding.
 */
function Standing({
  standing,
  apply,
}: {
  standing: RoleStanding;
  apply: { href: string; label: string } | null;
}): React.ReactElement {
  if (standing === 'active') return <>Approved</>;

  if (standing === 'applied') {
    return (
      <>
        Applied
        <br />
        {/*
          Said rather than left to be guessed at. Somebody waiting on a decision
          with no indication that anything is happening assumes the application
          was lost, and the next thing they do is send another one.
        */}
        <span className={ui.statLabel ?? ''}>We read every application by hand.</span>
      </>
    );
  }

  if (standing === 'suspended') {
    return (
      <>
        Suspended
        <br />
        <span className={ui.statLabel ?? ''}>
          <Link href="/contact">Ask us about it</Link>
        </span>
      </>
    );
  }

  if (!apply) return <>Not held</>;

  return (
    <Link href={apply.href} className={ui.buttonSecondary ?? ''}>
      {apply.label}
    </Link>
  );
}

/**
 * What this account can open.
 *
 * Read from `/me` rather than assumed from the host: a creator on
 * app.goodbuild.cc has a board whichever site's chrome they arrived under, and
 * a page that inferred it from the hostname would tell half of them they have
 * nothing.
 */
function Reach({ me }: { me: Me | undefined }): React.ReactElement | null {
  if (!me) return null;

  const places: { href: string; label: string; note: string }[] = [
    { href: '/library', label: 'Library', note: 'The games you own' },
    { href: '/orders', label: 'Orders', note: 'What you bought' },
  ];

  /*
    A `Studio portal` row was here, one per organisation the account could act
    for, and went with the portal (#144).
  */
  if (me.creator) {
    places.push({
      href: '/board',
      label: 'Your board',
      // The state matters: an applied creator holds the profile and not the
      // board, and section 3 is why — the rates on it are commercially
      // sensitive to developers.
      note: `@${me.creator.handle} — ${me.creator.state}`,
    });
  }

  if (me.isAdmin) {
    places.push({ href: '/admin', label: 'Admin', note: 'Staff screens' });
  }

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Where you can go</h2>
      <Table head={['Page', 'What it is']}>
        {places.map((place) => (
          <tr key={`${place.href}-${place.note}`}>
            <td>
              <Link href={place.href}>{place.label}</Link>
            </td>
            <td>{place.note}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}

function Help(): React.ReactElement | null {
  const support = supportAddress();
  if (support === null) return null;

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>If you need help</h2>
      <Table head={['', '']}>
        <tr>
          <td>Write to us</td>
          <td>
            <a href={`mailto:${support}`}>{support}</a>
            <br />
            <span className={ui.statLabel ?? ''}>
              A person reads everything sent here. We answer within three working days.
            </span>
          </td>
        </tr>
        <tr>
          <td>Refunds</td>
          <td>
            The button is in <Link href="/orders">what you bought</Link>, above your receipts. It
            shows only while a refund is still available.
            <br />
            <span className={ui.statLabel ?? ''}>
              <Link href="/legal/refunds">What you can get back, and when</Link>
            </span>
          </td>
        </tr>
        <tr>
          <td>Who billed you</td>
          <td>
            Creem is the merchant of record. Your statement says CREEM.IO* STORE.
            <br />
            <span className={ui.statLabel ?? ''}>
              <Link href="/legal/terms">Terms of use</Link> ·{' '}
              <Link href="/legal/privacy">Privacy policy</Link>
            </span>
          </td>
        </tr>
      </Table>
    </>
  );
}

/**
 * The machines signed in to this account.
 *
 * A device token is long-lived where a browser session is not, and the two
 * lifetimes are opposite deliberately — a launcher that signs a player out
 * every fortnight is a launcher they stop opening. What makes that affordable
 * is that each device is revocable on its own, from the player's own list, so a
 * stolen laptop costs one row rather than a password change. This is that list;
 * until now it existed only as an endpoint.
 */
function Devices(): React.ReactElement {
  const key = ['launcher', 'devices'] as const;
  const query = useApi<{ devices: Device[] }>(key, '/launcher/devices');
  const call = useApiMutation();
  const cache = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const devices = query.data?.devices ?? [];

  async function revoke(device: Device): Promise<void> {
    setBusy(device.id);
    setMessage(null);
    try {
      await call(`/launcher/devices/${device.id}`, { method: 'DELETE' });
      setMessage(`${device.name} was signed out. It will ask to be set up again.`);
      await cache.invalidateQueries({ queryKey: key });
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Your computers</h2>

      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {query.error ? (
        <Empty>{query.error.message}</Empty>
      ) : devices.length === 0 ? (
        <Empty>
          No computers are signed in. One shows up here when you set up the launcher on one.
        </Empty>
      ) : (
        <Table head={['Computer', 'Added', 'Last seen', '']}>
          {devices.map((device) => (
            <tr key={device.id}>
              <td>{device.name}</td>
              <td className={ui.numeric ?? ''}>{formatDate(new Date(device.createdAt))}</td>
              <td className={ui.numeric ?? ''}>
                {/*
                  "Never" rather than a dash: a device that registered and has
                  not come back is worth reading as a fact, because it is the
                  shape a duplicate row takes when a launcher lost its token
                  and registered again.
                */}
                {device.lastSeenAt ? formatDate(new Date(device.lastSeenAt)) : 'Never'}
              </td>
              <td>
                <button
                  type="button"
                  className={ui.buttonSecondary ?? ''}
                  disabled={busy === device.id}
                  onClick={() => void revoke(device)}
                >
                  Sign out
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

/**
 * Leaving.
 *
 * Two states rather than one: the first press asks, the second does it. Not
 * ceremony — this used to be a bare button in the navigation bar, one stray
 * click from every page and immediately beside links people use constantly, and
 * getting back in costs a password or a round trip through a provider.
 *
 * The sentence beside it is the part that was missing. Signing out here ends
 * this browser's session and nothing else; the launcher on a machine holds its
 * own token and keeps it, which is the correct behaviour and completely
 * invisible unless somebody says so.
 */
function SignOut(): React.ReactElement {
  const [asked, setAsked] = useState(false);
  const [going, setGoing] = useState(false);
  const request = useApiMutation();

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Signing out</h2>

      <p className="gb-body">
        This signs out this browser only. Computers running the launcher stay signed in — sign one
        out from the list above, which is also what to do if you lost it.
      </p>

      <p className="gb-body">
        <Link href="/forgot-password">Change your password</Link> to sign out everywhere at once.
      </p>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        {asked ? (
          <>
            <button
              type="button"
              className={ui.button ?? ''}
              disabled={going}
              onClick={() => {
                setGoing(true);
                /*
                 * The device registration goes first, and the order is the
                 * point.
                 *
                 * `revokeThisDevice` needs an authenticated request to revoke
                 * the row, so it has to happen while the session still works —
                 * and `endSession` is what stops it working. Reversed, this
                 * launcher would sign out still holding a live device token
                 * with nothing able to name it, which is the orphan the
                 * machines list exists to prevent. A no-op in a browser.
                 */
                void revokeThisDevice(request)
                  .then(() => endSession())
                  .then(() => window.location.reload());
              }}
            >
              {going ? 'Signing out…' : 'Yes, sign out'}
            </button>
            <button
              type="button"
              className={ui.buttonSecondary ?? ''}
              onClick={() => setAsked(false)}
            >
              Stay signed in
            </button>
          </>
        ) : (
          <button type="button" className={ui.buttonSecondary ?? ''} onClick={() => setAsked(true)}>
            Sign out
          </button>
        )}
      </div>
    </>
  );
}
